import { useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { QrCode, Download, Printer, Share2, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SiteFooter } from "./SiteFooter";
import { saveLastQr } from "@/lib/qrStorage";

interface QRCodeDownloadProps {
  onBack: () => void;
  onStartOver: () => void;
  qrCodePngUrl?: string;
  qrCodeSvgUrl?: string;
  qrCodeUrl?: string;
  mode?: "full" | "restored";
}

export function QRCodeDownload({
  onBack,
  onStartOver,
  qrCodePngUrl,
  qrCodeSvgUrl,
  qrCodeUrl,
  mode = "full",
}: QRCodeDownloadProps) {
  const { t } = useTranslation();
  const tips = t("qr.tips.items", { returnObjects: true }) as string[];
  const hasSavedRef = useRef(false);

  useEffect(() => {
    if (mode !== "full") return;
    if (!qrCodePngUrl || !qrCodeUrl) return;
    if (hasSavedRef.current) return;

    hasSavedRef.current = true;
    void saveLastQr(qrCodePngUrl, qrCodeUrl);
  }, [mode, qrCodePngUrl, qrCodeUrl]);

  const triggerDownload = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch file for download: ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      // Fail silently in the UI, but log for debugging.
      // As a last resort, fall back to opening in a new tab instead of breaking the page.
      console.error("QR download failed, falling back to browser behavior", error);
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleDownloadPNG = async () => {
    if (!qrCodePngUrl) return;
    await triggerDownload(qrCodePngUrl, "conciergie-qr.png");
  };

  const handleDownloadSVG = async () => {
    if (!qrCodeSvgUrl) return;
    await triggerDownload(qrCodeSvgUrl, "conciergie-qr.svg");
  };

  const handlePrint = () => {
    if (!qrCodeSvgUrl) return;
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      return;
    }

    printWindow.document.write(`<!DOCTYPE html>
<html>
  <head>
    <title>Print QR Code</title>
    <style>
      body {
        margin: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100vh;
      }
      img {
        max-width: 80vmin;
        max-height: 80vmin;
      }
      @page {
        margin: 10mm;
      }
    </style>
  </head>
  <body>
    <img src="${qrCodeSvgUrl}" alt="QR code" onload="window.focus(); window.print();" />
  </body>
</html>`);
    printWindow.document.close();
  };

  const handleShare = async () => {
    if (!qrCodeUrl) {
      return;
    }

    // Prefer sharing the image + link when supported.
    try {
      const shareText = t("qr.share.text", "Your digital guest directory link");
      const shareTitle = t("qr.share.titleText", "Conciergie guest directory");

      if (qrCodePngUrl && "canShare" in navigator && "share" in navigator) {
        try {
          const response = await fetch(qrCodePngUrl);
          const blob = await response.blob();
          const file = new File([blob], "conciergie-qr.png", { type: blob.type || "image/png" });

          const shareData: ShareData & { files?: File[] } = {
            title: shareTitle,
            text: shareText,
            url: qrCodeUrl,
            files: [file],
          };

          // Some implementations require an explicit canShare check for files.
          const anyNavigator = navigator as Navigator & {
            canShare?: (data: ShareData & { files?: File[] }) => boolean;
          };

          if (anyNavigator.canShare && anyNavigator.canShare(shareData)) {
            await navigator.share(shareData);
            return;
          }
        } catch {
          // If image fetch or file sharing fails, fall back to link-only share below.
        }
      }

      if ("share" in navigator) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: qrCodeUrl,
        });
        return;
      }

      if ("clipboard" in navigator && "writeText" in navigator.clipboard) {
        await navigator.clipboard.writeText(qrCodeUrl);
        // We don't have a toast system here, but copying silently is still useful.
      }
    } catch {
      // Swallow errors: worst case, the share does nothing instead of breaking the page.
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex flex-col">
      <div className="flex-1 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Button variant="ghost" onClick={onBack} className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("qr.back")}
          </Button>

          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <QrCode className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl mb-3">{t("qr.title")}</h1>
            <p className="text-lg text-gray-600">
              {t("qr.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* QR Code Preview */}
            <Card className="border-2">
              <CardContent className="pt-8 pb-8">
                <div className="text-center">
                  <h3 className="text-lg mb-6">{t("qr.preview.title")}</h3>

                  {/* QR Code Preview */}
                  <div className="bg-white p-8 rounded-lg shadow-lg inline-block mb-4 md:mb-6">
                    <div className="w-64 h-64 bg-gray-100 rounded-lg flex items-center justify-center border-4 border-gray-200 overflow-hidden">
                      {qrCodePngUrl ? (
                        <img
                          src={qrCodePngUrl}
                          alt={t("qr.preview.alt", "Hotel QR code")}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <QrCode className="h-32 w-32 text-gray-400" />
                      )}
                    </div>
                    <p className="mt-4 text-sm text-gray-600">
                      {qrCodePngUrl
                        ? t("qr.preview.captionReady", "Your QR code is ready to use.")
                        : t("qr.preview.caption")}
                    </p>
                  </div>

                  {qrCodeUrl && (
                    <div className="mt-3 md:mt-4">
                      <a
                        href={qrCodeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex w-full md:w-auto items-center justify-center rounded-md border border-green-200 bg-green-50 px-4 py-2 text-sm font-medium text-green-900 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                      >
                        {t(
                          "qr.preview.testLink",
                          "Open this link now to test your QR code on your phone"
                        )}
                      </a>
                      <p className="mt-2 text-xs text-gray-500 break-all md:break-normal">
                        {qrCodeUrl}
                      </p>
                    </div>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900">
                      {t("qr.preview.note")}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Download Options */}
            <div className="space-y-4">
              <Card>
                <CardContent className="pt-6 pb-6">
                  <h3 className="text-lg mb-4">{t("qr.download.title")}</h3>
                  <div className="space-y-3">
                    <Button
                      onClick={handleDownloadPNG}
                      disabled={!qrCodePngUrl}
                      className="w-full justify-start"
                      size="lg"
                    >
                      <Download className="h-5 w-5 mr-3" />
                      {t("qr.download.png")}
                    </Button>
                    {mode === "full" && (
                      <>
                        <Button
                          onClick={handleDownloadSVG}
                          variant="outline"
                          disabled={!qrCodeSvgUrl}
                          className="w-full justify-start"
                          size="lg"
                        >
                          <Download className="h-5 w-5 mr-3" />
                          {t("qr.download.svg")}
                        </Button>
                        <Button
                          onClick={handlePrint}
                          variant="outline"
                          disabled={!qrCodeSvgUrl}
                          className="w-full justify-start"
                          size="lg"
                        >
                          <Printer className="h-5 w-5 mr-3" />
                          {t("qr.download.print")}
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {mode === "full" && (
                <>
                  <Card>
                    <CardContent className="pt-6 pb-6">
                      <h3 className="text-lg mb-4">{t("qr.share.title")}</h3>
                      <div className="space-y-3">
                        <Button
                          onClick={handleShare}
                          variant="outline"
                          className="w-full justify-start"
                          disabled={!qrCodeUrl}
                          size="lg"
                        >
                          <Share2 className="h-5 w-5 mr-3" />
                          {t("qr.share.copy")}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gray-50">
                    <CardContent className="pt-6 pb-6">
                      <h3 className="text-lg mb-3">{t("qr.tips.title")}</h3>
                      <ul className="text-sm text-gray-600 space-y-2">
                        {tips.map((tip) => (
                          <li key={tip}>• {tip}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  <Button
                    onClick={onStartOver}
                    variant="outline"
                    className="w-full"
                    size="lg"
                  >
                    {t("qr.register")}
                  </Button>
                </>
              )}

              {mode === "restored" && (
                <Button
                  onClick={() => {
                    // Intentionally left as a no-op for now; will be wired when registration for
                    // restored flows is implemented.
                  }}
                  size="lg"
                  className="w-full h-14 text-lg shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  {t("qr.register")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}